import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../ts/L10n/L10n';
import { ISharedWithMeContentInfo, ISharedWithMeCustomViewInfo } from '../../../ts/models/ISharedWithMeContentInfo';
import { CardViewStyles } from '../CardView/CardViewStyles';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { renderSharerNameWithMessage, renderSharerProfilePicture } from '../CardView/SharedWithMeCard';
import { Name } from './Elements/Name';
import { Timestamp } from './Elements/Timestamp';
import { Tooltip } from './Elements/Tooltip';
import { User } from './Elements/User';
import { ContentTooltipProps, TooltipDescriptionTestId } from './TooltipHelper';
import { removeProjectDescriptionImage } from './ProjectTooltip';

export const MessageFromSharerTestId = 'tooltip-message-from-sharer';
export const DescriptionCharLimit = 100;
export const MessageCharLimit = 225;

export function SharedWithMeTooltip(props: ContentTooltipProps): JSX.Element {
  const content = props.content as ISharedWithMeContentInfo;
  return (
    <Tooltip>
      <Name name={content.name} />
      <div className={DetailsTooltipStyles.SharedWithMeContentDescription} data-tb-test-id={TooltipDescriptionTestId}>
        { contentDescription(content) }
      </div>
      <div className={DetailsTooltipStyles.SharedWithMeMetadata}>
        { renderSharerProfilePicture(content.sharer) }
        <div className={CardViewStyles.SharedWithMeSharerAndText}>
          <div>
            { renderSharerNameWithMessage(
                /** sharer */ content.sharer,
                /** urls */ props.urls,
                /** messageKey */ mapContentTypeToStringKey(content.contentType, content.customView),
                /** shownBelowThumbnail */ false,
                /** cv */ content.customView,
              )
            }
          </div>
          <div data-tb-test-id={MessageFromSharerTestId}>
            { content.message ? Messages.sharedWithMeMessage({ MESSAGE: L10n.elideIfLong(content.message, MessageCharLimit) }): '' }
          </div>
        </div>
      </div>
      <User user={content.owner} label={Messages.owner()} urls={props.urls} />
      <Timestamp label={Messages.modified()} value={content.updatedAt} />
    </Tooltip>
  );
}

function contentDescription(content: ISharedWithMeContentInfo): string {
  const fullDescription = content.contentType.toLowerCase() === 'project'
    ? removeProjectDescriptionImage(content.description)
    : content.description;
  return L10n.elideIfLong(fullDescription, DescriptionCharLimit);
}

function mapContentTypeToStringKey(contentType: string, customView?: ISharedWithMeCustomViewInfo): string {
  switch (contentType.toLowerCase()) {
    case 'view':
      // if the content has an associated custom view, use the string for custom/dirty view instead
      if (customView) {
        if (customView.isHidden) {
          return 'sharedWithMeTooltipForDirtyView';
        } else {
          return 'sharedWithMeTooltipForCustomView';
        }
      }
      return 'sharedWithMeTooltipForView';
    case 'project':
      return 'sharedWithMeTooltipForProject';
    case 'workbook':
      return 'sharedWithMeTooltipForWorkbook';
    case 'datasource':
      return 'sharedWithMeTooltipForDatasource';
    case 'flow':
      return 'sharedWithMeTooltipForFlow';
    case 'metric':
      return 'sharedWithMeTooltipForMetric';
    case 'publishedconnection':
      return 'sharedWithMeTooltipForPublishedConnection';
    case 'collection':
      return 'sharedWithMeTooltipForCollection';
    default:
      return 'shared';
  }
}
